import { AxiosRequestConfig } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { client } from 'src/utils/api-client';
import {
  GOODS_AND_SERVICES_OR_CAPITAL_GOODS,
  GhgSupplierSurvey,
  GhgSupplierGoods,
  GhgSupplierSurveySummary,
} from '../interfaces';
import {
  GoodsAndServicesData,
  GoodsAndServicesEmissionSharesData,
} from './types';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getFilenameDateSuffix } from 'src/utils/timeConverter';

// C14: additive country_codes/branch_descriptions alongside the existing
// `branches` slug list (C06 Goal 3). Kept as a local extension rather than
// widening GhgSupplierSurvey/GhgSupplierSurveySummary in interfaces.ts,
// which is outside this component's touched files.
export interface GhgSupplierSurveyWithCountry extends GhgSupplierSurvey {
  country_codes?: string[];
  branch_descriptions?: string[];
}

export interface GhgSupplierSurveySummaryWithCountry extends GhgSupplierSurveySummary {
  country_codes?: string[];
  branch_descriptions?: string[];
}

// R-06 pagination envelope -- mirrors GhgSupplierSurveyListEnvelopeSchema /
// GhgSupplierSurveySummaryEnvelopeSchema on the backend. Only returned when
// the caller passes `page`; omitting it keeps the legacy bare-list shape
// (C06 Goal 1b), which is exactly why `data`/`summary.data` below are typed
// as a union rather than always being the envelope.
export interface GhgSupplierSurveyPagedResult<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

function isPagedResult<T>(
  data: T[] | GhgSupplierSurveyPagedResult<T> | undefined
): data is GhgSupplierSurveyPagedResult<T> {
  return !!data && !Array.isArray(data);
}

// Normalizes the list/summary endpoints' dual response shape (bare array
// without `page`, envelope with it) into a consistent { items, total }
// pair. `total` is null when the caller didn't request pagination, since
// there's no server-computed total to report in that case.
export function unwrapSurveyList<T>(
  data: T[] | GhgSupplierSurveyPagedResult<T> | undefined
): { items: T[]; total: number | null } {
  if (!data) return { items: [], total: null };
  if (isPagedResult(data)) return { items: data.items, total: data.total };
  return { items: data, total: null };
}

// RG 28.08 / E-S4: the "selected companies" mode of the group dashboard.
// Branches are the primary scope; only when none are selected do the checked
// organizations narrow the data -- and then on the SERVER (`organization_ids`),
// so `total`, the pages and the exports all describe the same set. Returns
// undefined (no param at all) in branch mode, keeping those requests exactly
// as they were, and for an empty selection, which callers must render as
// "nothing" themselves rather than let the server answer "everything".
export const getOrganizationIdsParam = (
  branches: string[],
  organizationIds: number[]
): number[] | undefined =>
  branches.length === 0 && organizationIds.length > 0
    ? organizationIds
    : undefined;

// The same filter as a JSON body. axios leaves `undefined` and empty arrays out
// of a GET query string, so a filter the screen does not use is simply absent
// there; a body would carry `branches: []`, which the server reads as "no
// branches at all" (an empty result), not "no filter". Dropping them the same
// way makes both verbs ask the same question.
export const toSurveyFilterBody = (
  params: Record<string, unknown>
): Record<string, unknown> => {
  const body: Record<string, unknown> = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null) return;
    if (Array.isArray(value) && value.length === 0) return;
    body[key] = value;
  });
  return body;
};

// RG 28.08 review (E-S4): `organization_ids` of the "selected companies" mode
// can hold hundreds or thousands of ids -- about 22 bytes each as a query
// string, which overflows the nginx / ALB URL limit (414 / 400). That mode
// (`viaBody`) therefore asks the POST twin of the route (`<route>-filter`,
// same permissions and implementation on the server) with the filters as the
// body. Branch mode keeps the plain GET, request for request as before.
export function requestSurveys<T>(
  url: string,
  params: Record<string, unknown>,
  viaBody: boolean,
  config: AxiosRequestConfig = {}
) {
  return viaBody
    ? client.post<T>(`${url}-filter`, toSurveyFilterBody(params), config)
    : client.get<T>(url, { ...config, params });
}

export const useSupplierSurvey = (
  organizationId?: number,
  fromPeriod?: string,
  toPeriod?: string,
  branches?: string[],
  // C14: additive trailing params (countries/search/paging, C06's contract).
  // Kept optional and trailing so existing positional call sites that stop
  // at `branches` (Ghg/SupplierSummary) keep getting today's unfiltered,
  // unpaged bare-list/array response unchanged.
  countries?: string[],
  search?: string,
  page?: number,
  pageSize?: number,
  // RG 28.08 / E-S4 (K5): server-side "selected companies" filter, see
  // getOrganizationIdsParam. Applies to the list, the summary and all four
  // exports.
  organizationIds?: number[]
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const endpointUrl = `/web/ghg-suppliers/${organizationId}/survey`;
  // "Selected companies" mode: the filters (a long id list) go in a POST body,
  // see requestSurveys. Branch mode (undefined) is the unchanged GET.
  const viaBody = organizationIds !== undefined;
  const query = useQuery(
    [
      'ghg-supplier',
      'surveys',
      {
        organizationId,
        branches,
        countries,
        search,
        page,
        pageSize,
        fromPeriod,
        toPeriod,
        organizationIds,
      },
    ],
    () =>
      requestSurveys<
        | GhgSupplierSurveyWithCountry[]
        | GhgSupplierSurveyPagedResult<GhgSupplierSurveyWithCountry>
      >(
        `${endpointUrl}/all`,
        {
          branches,
          organization_ids: organizationIds,
          countries,
          search,
          page,
          page_size: pageSize,
          // from_period/to_period moved onto the base filter schema
          // (backend d615e3953) specifically so the list route -- now
          // server-paginated (C14) -- can filter by year again; the
          // "Rok" selector drives this exactly like it already drove
          // summary/exports.
          from_period: fromPeriod,
          to_period: toPeriod,
        },
        viaBody
      ).then((response) => response.data),
    {
      enabled: !!organizationId,
    }
  );

  const summary = useQuery(
    [
      'ghg-supplier',
      'surveys-summary',
      {
        organizationId,
        fromPeriod,
        toPeriod,
        branches,
        countries,
        search,
        organizationIds,
      },
    ],
    () =>
      requestSurveys<GhgSupplierSurveySummaryWithCountry[]>(
        `${endpointUrl}/summary`,
        {
          from_period: fromPeriod,
          to_period: toPeriod,
          branches,
          organization_ids: organizationIds,
          countries,
          search,
        },
        viaBody
      ).then((response) => response.data),
    {
      enabled: !!organizationId && !!fromPeriod && !!toPeriod,
    }
  );

  // Goal 4 / EC-1: every export must carry the SAME active filters the
  // screen is showing (countries/branches/search/period) so the downloaded
  // file matches the visible table -- the backend (C03/C06) already scopes
  // on these for all four exports, this just forwards them. Deliberately
  // excludes page/page_size: the export endpoints always return the full
  // filtered set, never a single page. `organization_ids` (E-S4) rides along
  // for the same reason: in "selected companies" mode the file must hold
  // exactly the companies the table shows.
  const exportFilterParams = {
    from_period: fromPeriod,
    to_period: toPeriod,
    branches,
    organization_ids: organizationIds,
    countries,
    search,
  };

  const create = useMutation({
    mutationFn: (data: {
      product_name: string;
      supplier_name: string;
      vat_identifier: string;
      reporting_period: string;
      organization_id: number;
    }) => client.post(endpointUrl, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['ghg-supplier']);
    },
  });

  const remove = useMutation({
    mutationFn: (surveyId: number) =>
      client.delete(`${endpointUrl}/${surveyId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['ghg-supplier']);
    },
  });

  const exportCsv = useMutation({
    mutationFn: () =>
      requestSurveys<Blob>(
        `${endpointUrl}/export-csv`,
        exportFilterParams,
        viaBody,
        { responseType: 'blob' }
      ),
    onSuccess: (response) => {
      const currentDate = getFilenameDateSuffix();
      const fileName = `suppliers-summary-${currentDate}.csv`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.download = fileName;
      link.click();
    },
    onError: () => {
      toast.error(t('esg.downloadError') as string, {
        autoClose: 10_000,
        toastId: 'submit-error',
      });
    },
  });

  const exportAllAnswers = useMutation({
    mutationFn: () =>
      // Previously sent no params at all, so a scoped caller's export
      // silently contained the whole org regardless of any active filter --
      // fixed here to match the other three exports (Goal 4).
      requestSurveys<Blob>(
        `${endpointUrl}/export-all-answers-xlsx`,
        exportFilterParams,
        viaBody,
        { responseType: 'blob' }
      ),
    onSuccess: (response) => {
      const currentDate = getFilenameDateSuffix();
      const fileName = `suppliers-all-answers-${currentDate}.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.download = fileName;
      link.click();
    },
    onError: () => {
      toast.error(t('esg.downloadError') as string, {
        autoClose: 10_000,
        toastId: 'submit-error',
      });
    },
  });

  const exportPivotCsv = useMutation({
    mutationFn: () =>
      requestSurveys<Blob>(
        `${endpointUrl}/export-pivot-csv`,
        exportFilterParams,
        viaBody,
        { responseType: 'blob' }
      ),
    onSuccess: (response) => {
      const currentDate = getFilenameDateSuffix();
      const fileName = `suppliers-pivot-data-${currentDate}.csv`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.download = fileName;
      link.click();
    },
    onError: () => {
      toast.error(t('esg.downloadError') as string, {
        autoClose: 10_000,
        toastId: 'submit-error',
      });
    },
  });

  const exportWideCsv = useMutation({
    mutationFn: () =>
      requestSurveys<Blob>(
        `${endpointUrl}/export-wide-csv`,
        exportFilterParams,
        viaBody,
        { responseType: 'blob' }
      ),
    onSuccess: (response) => {
      const currentDate = getFilenameDateSuffix();
      const fileName = `suppliers-wide-format-${currentDate}.csv`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.data);
      link.download = fileName;
      link.click();
    },
    onError: () => {
      toast.error(t('esg.downloadError') as string, {
        autoClose: 10_000,
        toastId: 'submit-error',
      });
    },
  });

  // Unwrapped here, once, so `data` keeps its pre-C14 type/shape (a plain
  // array, or undefined while loading) for every existing caller -- several
  // of which (Ghg/SupplierSummary, ProductsFromSuppliers/PreviewDialog,
  // views/components/CompanyFilter) are outside this component's touched
  // files and never pass `page`, so they must keep compiling and behaving
  // exactly as before. `total` is additive: undefined/null unless the
  // caller opted into paging.
  const { items: surveyItems, total: surveyTotal } = unwrapSurveyList(
    query.data
  );

  return {
    ...query,
    data: surveyItems,
    total: surveyTotal,
    summary,
    create,
    remove,
    exportCsv,
    exportAllAnswers,
    exportPivotCsv,
    exportWideCsv,
  };
};

export const useSupplierGoods = (
  organizationId: number,
  filter: string = ''
) => {
  const queryClient = useQueryClient();
  const endpointUrl = `/web/ghg-suppliers/${organizationId}/goods`;
  const query = useQuery(
    ['ghg-supplier', 'goods', { organizationId, filter }],
    () =>
      client
        .get<GhgSupplierGoods[]>(`${endpointUrl}/all${filter}`)
        .then((response) => response.data)
  );
  const create = useMutation({
    mutationFn: (data: {
      survey_answer_id: number;
      amount: number;
      type: GOODS_AND_SERVICES_OR_CAPITAL_GOODS;
      reporting_period: string;
    }) => client.post(endpointUrl, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['ghg-supplier']);
      query.refetch();
    },
  });

  const update = useMutation({
    mutationFn: (data: {
      id: number;
      amount: number;
      type: GOODS_AND_SERVICES_OR_CAPITAL_GOODS;
      reporting_period: string;
    }) => client.put(`${endpointUrl}/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['ghg-supplier']);
      query.refetch();
    },
  });

  const remove = useMutation({
    mutationFn: (reportingPeriodId: number) =>
      client.delete(`${endpointUrl}/${reportingPeriodId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['ghg-supplier']);
      query.refetch();
    },
  });

  return { query, create, update, remove };
};

type UseGoodsAndServicesProps = {
  type: GOODS_AND_SERVICES_OR_CAPITAL_GOODS;
  period: string;
  organization_id: number;
};

export const useGoodsAndServices = (props: UseGoodsAndServicesProps) => {
  const queryClient = useQueryClient();

  const qtype =
    props.type === GOODS_AND_SERVICES_OR_CAPITAL_GOODS.GOODS_AND_SERVICES
      ? 'PurchasedGoodsAndSerivesData'
      : 'CapitalGoodsData';
  const baseUrl = `/web/v2/questionnaires/${qtype}`;
  const queryKey = ['ghg', 'goods-and-services', props];

  const params = {
    period: props.period,
    organization_id: props.organization_id,
  };

  const query = useQuery(queryKey, () =>
    client.get<GoodsAndServicesData>(baseUrl, { params }).then((r) => r.data)
  );

  const queryEmissions = useQuery([...queryKey, 'emissions'], () =>
    client
      .get<GoodsAndServicesEmissionSharesData>(`${baseUrl}/emission_shares`, {
        params,
      })
      .then((r) => r.data)
  );

  const refetch = () =>
    queryClient.invalidateQueries(['ghg', 'goods-and-services']);

  const update = useMutation({
    mutationFn: (data: GoodsAndServicesData) =>
      client.put(baseUrl, data, { params }),
    onSuccess: refetch,
  });

  const isLoading = query.isLoading || queryEmissions.isLoading;
  const isError = query.isError || queryEmissions.isError;

  return {
    query,
    queryEmissions,
    update,
    isLoading,
    isError,
    refetch,
  };
};
